import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAxiosError } from "axios";
import {
  COMMUNICATION_SKILLS_LESSON_NO,
  getCanDosWithTopics,
  getStudentProgressMe,
  type SkillProgressEntry,
} from "../../services/api/canDo";
import type { CanDoItem, CommunicationTopicMeta } from "../../modules/tutor/api/tutor.client";
import { getStudentMe, resolveStudentGrade, type RosterStudent } from "../../modules/roster/api/roster.client";
import { ROUTES } from "../../utils/constants";
import { Loader2, Lock, Sparkles, Star, Check } from "lucide-react";

/** Mean rubric ≥ this counts as "Mastered" for bubbly trail. */
const MASTER_THRESHOLD = 0.75;

const NAV_GRADIENT =
  "bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 hover:brightness-110 shadow-lg shadow-purple-500/25";

/** Progress/fills aligned with app chrome (header gradient), not a separate orange accent. */
const ACCENT_GRADIENT_H = "bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-400";
const ACCENT_GRADIENT_V = "bg-gradient-to-t from-indigo-600 via-purple-500 to-fuchsia-400";

type BubbleStatus = "mastered" | "in_progress" | "locked";

type CanDoBubbleModel = {
  can_do_id: string;
  statement: string;
  fluency: number;
  accuracy: number;
  appropriateness: number;
  status: BubbleStatus;
  overallPct: number;
};

type TopicProgressGroup = {
  topicId: string;
  topicTitle: string;
  topicHint?: string;
  bubbles: CanDoBubbleModel[];
};

function meanPerformance(p: SkillProgressEntry["avg_performance"]): number {
  return (p.fluency + p.accuracy + p.appropriateness) / 3;
}

function overallPctFromPerf(p: SkillProgressEntry["avg_performance"]): number {
  return Math.round(meanPerformance(p) * 100);
}

function groupCurriculumWithProgress(
  communicationTopics: CommunicationTopicMeta[],
  canDos: CanDoItem[],
  skills: SkillProgressEntry[]
): TopicProgressGroup[] {
  const skillMap = new Map(skills.map((s) => [s.can_do_id, s]));
  const byTopic = new Map<string, CanDoItem[]>();
  for (const cd of canDos) {
    const tid = cd.communication_topic_id || "_ungrouped";
    if (!byTopic.has(tid)) byTopic.set(tid, []);
    byTopic.get(tid)!.push(cd);
  }

  const rowForCanDo = (cd: CanDoItem): CanDoBubbleModel => {
    const skill = skillMap.get(cd.id);
    const statement =
      (skill?.statement || "").trim() ||
      (cd.statement_en || "").trim() ||
      (cd.statement_ta || "").trim() ||
      "Can-Do goal";
    if (!skill) {
      return {
        can_do_id: cd.id,
        statement,
        fluency: 0,
        accuracy: 0,
        appropriateness: 0,
        status: "locked",
        overallPct: 0,
      };
    }
    const mean = meanPerformance(skill.avg_performance);
    const overallPct = overallPctFromPerf(skill.avg_performance);
    return {
      can_do_id: cd.id,
      statement,
      fluency: skill.avg_performance.fluency,
      accuracy: skill.avg_performance.accuracy,
      appropriateness: skill.avg_performance.appropriateness,
      status: mean >= MASTER_THRESHOLD ? "mastered" : "in_progress",
      overallPct,
    };
  };

  const groups: TopicProgressGroup[] = [];

  for (const t of communicationTopics) {
    const group = byTopic.get(t.id);
    if (!group?.length) continue;
    groups.push({
      topicId: t.id,
      topicTitle: (t.title_en || "").trim() || "Topic",
      topicHint: (t.title_ta || "").trim() || undefined,
      bubbles: group.map(rowForCanDo),
    });
  }

  const ungrouped = byTopic.get("_ungrouped") || [];
  for (const cd of ungrouped) {
    groups.push({
      topicId: cd.id,
      topicTitle:
        (cd.statement_en || "").trim().replace(/^I can\s+/i, "").replace(/\.$/, "") || "Practice goal",
      topicHint: (cd.statement_ta || "").trim() || undefined,
      bubbles: [rowForCanDo(cd)],
    });
  }

  return groups;
}

function fallbackTopicFromSkills(skills: SkillProgressEntry[]): TopicProgressGroup {
  return {
    topicId: "_all",
    topicTitle: "Communication practice",
    topicHint: "Your logged Can-Do averages",
    bubbles: skills.map((s) => {
      const mean = meanPerformance(s.avg_performance);
      const overallPct = overallPctFromPerf(s.avg_performance);
      return {
        can_do_id: s.can_do_id,
        statement: (s.statement || "").trim() || "Can-Do goal",
        fluency: s.avg_performance.fluency,
        accuracy: s.avg_performance.accuracy,
        appropriateness: s.avg_performance.appropriateness,
        status: mean >= MASTER_THRESHOLD ? "mastered" : "in_progress",
        overallPct,
      };
    }),
  };
}

function masterySummary(bubbles: CanDoBubbleModel[]): { mastered: number; total: number } {
  const total = bubbles.length;
  const mastered = bubbles.filter((b) => b.status === "mastered").length;
  return { mastered, total };
}

/** Three thick vertical metric pills (0–100%). */
function MetricPillBars({
  fluency,
  accuracy,
  appropriateness,
  muted,
}: {
  fluency: number;
  accuracy: number;
  appropriateness: number;
  muted?: boolean;
}) {
  const bars = [
    { short: "Flu", label: "Fluency", v: Math.round(fluency * 100) },
    { short: "Acc", label: "Accuracy", v: Math.round(accuracy * 100) },
    { short: "App", label: "Appropriateness", v: Math.round(appropriateness * 100) },
  ];
  return (
    <div className="flex gap-2 shrink-0 items-end" aria-label="Fluency, accuracy, appropriateness">
      {bars.map(({ short, label, v }) => (
        <div key={label} className="flex flex-col items-center gap-1" title={`${label}: ${v}%`}>
          <div
            className={`relative flex h-[52px] w-3.5 justify-center overflow-hidden rounded-full border ${
              muted
                ? "border-slate-200 bg-slate-100 dark:border-white/15 dark:bg-white/5"
                : "border-purple-300 bg-white dark:border-purple-400/35 dark:bg-neutral-900"
            }`}
          >
            <div
              className={`absolute bottom-0 left-0 right-0 rounded-full transition-all duration-500 ${
                muted ? "bg-slate-300/80 dark:bg-white/15" : ACCENT_GRADIENT_V
              }`}
              style={{ height: `${muted ? 8 : Math.max(8, v)}%` }}
            />
          </div>
          <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 dark:text-neutral-500">
            {short}
          </span>
        </div>
      ))}
    </div>
  );
}

function CanDoBubbleCard({ bubble }: { bubble: CanDoBubbleModel }) {
  const { status, statement, fluency, accuracy, appropriateness, overallPct } = bubble;

  const baseLift =
    "transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl snap-start shrink-0";

  if (status === "locked") {
    return (
      <div
        className={`${baseLift} flex min-w-[min(100%,280px)] max-w-[280px] flex-col gap-3 rounded-[1.75rem] border border-slate-200/90 bg-white/90 p-4 shadow-md shadow-slate-200/60 backdrop-blur-md dark:border-white/10 dark:bg-white/[0.06] dark:shadow-lg dark:shadow-black/20`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:bg-white/10 dark:text-neutral-400">
              <Lock className="h-3 w-3" aria-hidden />
              Locked
            </span>
            <p className="text-sm font-semibold leading-snug text-slate-800 line-clamp-3 dark:text-neutral-300">
              {statement}
            </p>
          </div>
          <MetricPillBars fluency={fluency} accuracy={accuracy} appropriateness={appropriateness} muted />
        </div>
        <p className="text-[11px] text-slate-500 dark:text-neutral-500">
          Open the Communication Tutor on this topic to unlock progress.
        </p>
      </div>
    );
  }

  if (status === "mastered") {
    return (
      <div
        className={`${baseLift} flex min-w-[min(100%,280px)] max-w-[280px] flex-col gap-3 rounded-[1.75rem] border-2 border-purple-300 bg-gradient-to-br from-purple-100/95 via-indigo-50/90 to-white p-4 shadow-lg shadow-purple-200/50 dark:border-purple-400/70 dark:from-purple-500/20 dark:via-indigo-600/15 dark:to-neutral-950 dark:shadow-[0_12px_40px_-12px_rgba(168,85,247,0.4)]`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm shadow-purple-500/30">
              <Check className="h-3.5 w-3.5 stroke-[3]" aria-hidden />
              Mastered
            </span>
            <p className="text-sm font-bold leading-snug text-slate-900 line-clamp-3 dark:text-white">{statement}</p>
          </div>
          <Star className="h-5 w-5 shrink-0 fill-pink-500 text-fuchsia-500 drop-shadow-sm dark:fill-pink-400 dark:text-fuchsia-300 dark:drop-shadow-[0_0_8px_rgba(232,121,249,0.5)]" aria-hidden />
        </div>
        <div className="flex items-end justify-between gap-2 border-t border-purple-200 pt-3 dark:border-purple-500/25">
          <MetricPillBars fluency={fluency} accuracy={accuracy} appropriateness={appropriateness} />
          <span className="text-2xl font-black tabular-nums bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent dark:from-fuchsia-300 dark:via-pink-400 dark:to-pink-400">
            {overallPct}%
          </span>
        </div>
      </div>
    );
  }

  /* in_progress */
  return (
    <div
      className={`${baseLift} relative flex min-w-[min(100%,280px)] max-w-[280px] flex-col gap-3 overflow-hidden rounded-[1.75rem] border border-purple-200 bg-white p-4 shadow-md shadow-slate-200/70 dark:border-purple-500/35 dark:bg-neutral-900 dark:shadow-[0_12px_36px_-14px_rgba(76,29,149,0.35)]`}
    >
      <div
        className="pointer-events-none absolute inset-0 rounded-[1.75rem] bg-gradient-to-br from-purple-400/[0.12] via-fuchsia-400/[0.08] to-transparent opacity-95 dark:from-purple-500/[0.14] dark:via-fuchsia-500/[0.06]"
        style={{
          clipPath: `inset(${100 - overallPct}% 0 0 0 round 1.75rem)`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-3 bottom-3 top-12 rounded-2xl bg-purple-400/10 animate-pulse dark:bg-purple-500/10"
        style={{ animationDuration: "2.2s" }}
      />
      <div className="relative flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <span className="inline-flex w-fit items-center gap-1 rounded-full border border-purple-300 bg-purple-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-purple-800 dark:border-purple-400/45 dark:bg-purple-500/15 dark:text-purple-200">
            <Sparkles className="h-3 w-3 text-purple-600 dark:text-fuchsia-400" aria-hidden />
            In progress
          </span>
          <p className="text-sm font-semibold leading-snug text-slate-800 line-clamp-3 dark:text-neutral-100">{statement}</p>
        </div>
        <MetricPillBars fluency={fluency} accuracy={accuracy} appropriateness={appropriateness} />
      </div>
      <div className="relative flex items-center justify-between gap-2 border-t border-slate-200 pt-3 dark:border-white/10">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200 ring-1 ring-purple-200/80 dark:bg-neutral-800 dark:ring-purple-500/15">
          <div
            className={`h-full rounded-full ${ACCENT_GRADIENT_H} transition-all duration-700 ease-out`}
            style={{ width: `${overallPct}%` }}
          />
        </div>
        <span className="text-lg font-black tabular-nums bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent dark:from-indigo-300 dark:via-purple-300 dark:to-pink-300">
          {overallPct}%
        </span>
      </div>
    </div>
  );
}

/**
 * Student-only: topic-wise Communication Can-Do trail with bubbly high-contrast UI.
 * Progress from GET /v1/tutor/student-progress/me; curriculum from lesson 0 bundle + roster grade.
 */
export const CommunicationCanDoProgressCard: React.FC = () => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<"loading" | "forbidden" | "ready">("loading");
  const [topics, setTopics] = useState<TopicProgressGroup[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      let skills: SkillProgressEntry[] = [];
      try {
        const res = await getStudentProgressMe();
        if (cancelled) return;
        skills = res.skills ?? [];
      } catch (e: unknown) {
        if (cancelled) return;
        if (isAxiosError(e) && (e.response?.status === 403 || e.response?.status === 401)) {
          setPhase("forbidden");
          return;
        }
        setPhase("forbidden");
        return;
      }

      let groups: TopicProgressGroup[] = [];
      try {
        const me = await getStudentMe();
        if (cancelled) return;
        const grade = resolveStudentGrade(me as RosterStudent);
        if (grade != null) {
          const { canDos, communicationTopics } = await getCanDosWithTopics(
            grade,
            COMMUNICATION_SKILLS_LESSON_NO
          );
          if (cancelled) return;
          groups = groupCurriculumWithProgress(communicationTopics, canDos, skills);
        }
      } catch {
        /* roster/bundle optional */
      }

      if (cancelled) return;

      if (groups.length === 0 && skills.length > 0) {
        groups = [fallbackTopicFromSkills(skills)];
      }

      setTopics(groups);
      if (groups.length > 0) {
        const withProgress = groups.find((g) => g.bubbles.some((b) => b.status !== "locked"));
        setSelectedTopicId((withProgress ?? groups[0]).topicId);
      } else {
        setSelectedTopicId(null);
      }
      setPhase("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeTopic = useMemo(
    () => topics.find((t) => t.topicId === selectedTopicId) ?? topics[0] ?? null,
    [topics, selectedTopicId]
  );

  const summary = useMemo(
    () => (activeTopic ? masterySummary(activeTopic.bubbles) : { mastered: 0, total: 0 }),
    [activeTopic]
  );

  if (phase === "forbidden") return null;

  if (phase === "loading") {
    return (
      <section className="mt-8 rounded-[1.75rem] border border-purple-200 bg-white p-6 shadow-lg shadow-purple-200/30 dark:border-purple-500/25 dark:bg-neutral-950 dark:shadow-[0_20px_60px_-20px_rgba(147,51,234,0.28)]">
        <div className="flex items-center gap-3 text-slate-600 dark:text-purple-100/85">
          <Loader2 className="h-6 w-6 shrink-0 animate-spin text-purple-600 dark:text-purple-400" aria-hidden />
          <span className="text-sm font-medium">Loading your Communication Can-Do trail…</span>
        </div>
      </section>
    );
  }

  const emptyNoTopics = topics.length === 0;

  return (
    <section className="mt-8 rounded-[1.75rem] border border-purple-200 bg-white p-5 shadow-xl shadow-purple-200/25 sm:p-7 dark:border-purple-500/30 dark:bg-neutral-950 dark:shadow-[0_24px_64px_-24px_rgba(147,51,234,0.35)]">
      <style>{`
        .can-do-trail-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(139,92,246,0.45) rgb(241 245 249);
        }
        .dark .can-do-trail-scroll {
          scrollbar-color: rgba(167,139,250,0.55) rgba(255,255,255,0.06);
        }
        .can-do-trail-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .can-do-trail-scroll::-webkit-scrollbar-track {
          background: rgb(241 245 249);
          border-radius: 999px;
        }
        .dark .can-do-trail-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.06);
        }
        .can-do-trail-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, rgba(99,102,241,0.55), rgba(168,85,247,0.5), rgba(236,72,153,0.5));
          border-radius: 999px;
        }
        .dark .can-do-trail-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(90deg, rgba(99,102,241,0.65), rgba(168,85,247,0.55), rgba(236,72,153,0.55));
        }
      `}</style>

      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 dark:border-white/10 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-xl font-extrabold tracking-tight text-slate-900 sm:text-2xl dark:text-white">
            Communication Can-Do progress
          </h2>
          {activeTopic ? (
            <>
              <p className="text-lg font-bold">
                <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent dark:from-indigo-300 dark:via-purple-300 dark:to-pink-300">
                  Topic:
                </span>{" "}
                <span className="text-slate-900 dark:text-white">{activeTopic.topicTitle}</span>
              </p>
              {activeTopic.topicHint ? (
                <p className="text-sm text-slate-600 dark:text-neutral-400">{activeTopic.topicHint}</p>
              ) : null}
              <p className="text-sm font-semibold text-slate-700 dark:text-neutral-300">
                <span className="font-bold text-purple-600 dark:text-fuchsia-400">{summary.mastered}</span> of{" "}
                <span className="text-slate-900 dark:text-white">{summary.total}</span> mastered
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-600 dark:text-neutral-400">
              Your topic-wise speaking goals will appear here.
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            const base = ROUTES.STUDENT_COMMUNICATION_TUTOR;
            const q =
              activeTopic && activeTopic.topicId && !activeTopic.topicId.startsWith("_")
                ? `?topic_id=${encodeURIComponent(activeTopic.topicId)}`
                : "";
            navigate(`${base}${q}`);
          }}
          className={`inline-flex shrink-0 items-center justify-center rounded-full px-6 py-3 text-sm font-bold text-white transition hover:scale-[1.02] active:scale-[0.98] ${NAV_GRADIENT}`}
        >
          Level Up
        </button>
      </header>

      {topics.length > 1 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {topics.map((t) => {
            const { mastered, total } = masterySummary(t.bubbles);
            const active = t.topicId === selectedTopicId;
            return (
              <button
                key={t.topicId}
                type="button"
                onClick={() => setSelectedTopicId(t.topicId)}
                className={`rounded-full border px-4 py-2 text-left text-xs font-bold transition hover:-translate-y-0.5 sm:text-sm ${
                  active
                    ? "border-purple-400 bg-gradient-to-r from-indigo-100 via-purple-100 to-pink-100 text-indigo-950 shadow-md shadow-purple-200/60 ring-1 ring-purple-300/80 dark:border-purple-400/80 dark:from-indigo-600/25 dark:via-purple-600/25 dark:to-pink-600/20 dark:text-white dark:shadow-lg dark:shadow-indigo-500/20 dark:ring-purple-400/30"
                    : "border-slate-200 bg-slate-50 text-slate-700 hover:border-purple-300 dark:border-white/10 dark:bg-white/[0.06] dark:text-neutral-300 dark:hover:border-purple-500/40"
                }`}
              >
                {t.topicTitle}
                <span className="ml-2 tabular-nums text-slate-500 dark:text-neutral-500">
                  ({mastered}/{total})
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="mt-5">
        {emptyNoTopics ? (
          <p className="rounded-[1.5rem] border border-dashed border-purple-200 bg-slate-50 px-5 py-10 text-center text-sm text-slate-600 dark:border-purple-500/35 dark:bg-white/[0.03] dark:text-neutral-400">
            Complete a conversation in the Communication Tutor to unlock your bubbly progress trail. Tap{" "}
            <span className="font-semibold bg-gradient-to-r from-indigo-600 to-pink-500 bg-clip-text text-transparent dark:from-purple-300 dark:to-pink-300">
              Level Up
            </span>{" "}
            to practice.
          </p>
        ) : activeTopic ? (
          <>
            <p className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-neutral-500">
              Can-Do trail — swipe sideways on mobile
            </p>
            <div className="can-do-trail-scroll -mx-1 flex gap-4 overflow-x-auto px-1 pb-3 pt-1 snap-x snap-mandatory">
              {activeTopic.bubbles.map((b) => (
                <CanDoBubbleCard key={b.can_do_id} bubble={b} />
              ))}
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
};
